import log from 'loglevel'
import { TippedIconClusterLayer } from '@/js/custom-layers/TippedIconClusterLayer.js'

// レイヤーID
const LAYER_ID = 'tippedIcon'

/**
 * ラベルの文字サイズ（従来のラベル画像と同じ）
 * @type {Number}
 */
const LABEL_FONT_SIZE = 14

/**
 * ラベルの文字色（従来のラベル画像と同じ #333333）
 * @type {Array.<Number>}
 */
const LABEL_TEXT_COLOR = [51, 51, 51, 255]

/**
 * ラベルの背景色（従来のラベル画像と同じ #ffffff）
 * @type {Array.<Number>}
 */
const LABEL_BACKGROUND_COLOR = [255, 255, 255, 255]

/**
 * ユーザ名ラベル付きマーカーのレンダラー
 */
export default class TippedIconRenderer {
  /**
   * コンストラクタ
   */
  constructor({ layerId } = {}) {
    this.layerId = layerId || LAYER_ID
  }

  /**
   * レンダラー関数を作成する
   * @param {Array.<Object>} data データ
   * @param {Object} props 上書きするprops
   * @returns {Object} レンダラー {id: String, render: Function.<IconClusterLayer>}
   */
  createRenderer(data, props) {
    log.debug(`[DEBUG] start render ${this.layerId}`, data, props)

    const iconProps = {
      fontWeight: 'bold',
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", "游ゴシック Medium", "Yu Gothic", YuGothic, YuGothicMedium, "Hiragino Kaku Gothic ProN", Meiryo, sans-serif',
      fontSettings: {
        sdf: true,
      },
      lineHeight: 1.4,
      getIconPixelOffset: (d) => {
        // 混在クラスタは縦に大きいため、表示位置を少し上に補正する
        if (d.properties.cluster && d.properties.user_type === 'mixed') {
          return [0, -8]
        }
        return [0, 0]
      },
      getIcon: (d) => (d.properties.cluster ? 'cluster' : 'default'),
      getIconSize: (d) => {
        if (!d.properties.cluster) return 64
        // 混在クラスタの場合は縦長のアイコンサイズを返す
        if (d.properties.user_type === 'mixed') {
          return [80, 53] // 240x160 -> 80x53.33
        }
        return [80, 40] // 240x120 -> 80x40
      },
      // v9対応: [x, y] 固定値を関数化して、サブレイヤー内での呼び出しエラーを防止
      getTextPixelOffset: () => [16, 4],
      // v9対応: テキストの垂直方向の配置基準を中央に設定
      getTextAlignmentBaseline: 'center',
      // v9対応: 固定値を関数化
      getTextSize: () => 16,
      // v9対応: getTextが指定されていない場合のデフォルトの挙動を定義
      getText: (d) =>
        d.properties.cluster ? `${d.properties.point_count}` : '',
      // v9対応: [r, g, b] 固定値を関数化（getColor is not a function エラー対策）
      getTextColor: () => [255, 255, 255],
      pickable: true,
      radius: 88,
      sizeScale: 1,
    }
    const labelProps = {
      // マーカーの高さぶん上に出す
      labelPixelOffset: [0, -56],
      getLabelSize: () => LABEL_FONT_SIZE,
      // アトラスをこの大きさで焼く（縮小によるにじみを避ける）
      labelMaxFontSize: LABEL_FONT_SIZE,
      getLabelColor: LABEL_TEXT_COLOR,
      getLabelBackgroundColor: LABEL_BACKGROUND_COLOR,
    }

    return {
      id: this.layerId,
      render: () =>
        new TippedIconClusterLayer({
          id: this.layerId,
          data,
          billboard: true,
          parameters: {
            depthTest: false,
            blend: true,
          },
          ...iconProps,
          ...labelProps,
          ...props,
        }),
    }
  }
}
